import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { FiTrendingUp, FiBarChart2, FiActivity, FiDownload, FiFilter, FiPieChart, FiUsers, FiMessageSquare, FiClock } from 'react-icons/fi';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import api from '../utils/api';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Title, Tooltip, Legend, Filler);

type Trends = { labels: string[]; data: number[] };
type LabeledSeries = { labels: string[]; data: number[] };

const normalizePeriod = (p: string) => (p === '1y' ? '365d' : p);

const Analytics: React.FC = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('7d');
  const [loading, setLoading] = useState(false);

  // Metrics
  const [activeAssistants, setActiveAssistants] = useState(0);
  const [totalConversations, setTotalConversations] = useState(0);
  const [messagesToday, setMessagesToday] = useState(0);
  const [successRate, setSuccessRate] = useState(0);
  const [avgResponseMs, setAvgResponseMs] = useState(0);
  const [activeConnections, setActiveConnections] = useState(0);

  // Charts
  const [trends, setTrends] = useState<Trends>({ labels: [], data: [] });
  const [channels, setChannels] = useState<LabeledSeries>({ labels: [], data: [] });
  const [outcomes, setOutcomes] = useState<LabeledSeries>({ labels: [], data: [] });
  const [messageActivity, setMessageActivity] = useState<Trends>({ labels: [], data: [] });

  // Activity feed
  const [activities, setActivities] = useState<Array<{ id: string; assistantName: string; lastMessage: string; timestamp: string }>>([]);

  const loadAll = async () => {
    try {
      setLoading(true);
      const p = normalizePeriod(selectedPeriod);
      const [statsRes, trendsRes, channelsRes, callStatsRes, activityRes, msgActivityRes] = await Promise.all([
        api.get('/analytics/dashboard-stats'),
        api.get(`/analytics/conversation-trends?period=${p}`),
        api.get('/analytics/channel-performance'),
        api.get(`/analytics/call-stats?period=${p}`),
        api.get('/analytics/activity-feed?limit=10'),
        api.get('/analytics/message-activity')
      ]);

      const s = statsRes.data || {};
      setActiveAssistants(s.activeAssistants || 0);
      setTotalConversations(s.totalConversations || 0);
      setMessagesToday(s.messagesToday || 0);
      setSuccessRate(s.successRate || 0);
      setAvgResponseMs(s.performance?.avgResponseTimeMs || 0);
      setActiveConnections(s.performance?.activeConnections || 0);

      setTrends({ labels: trendsRes.data?.labels || [], data: trendsRes.data?.data || [] });
      setChannels({ labels: channelsRes.data?.labels || [], data: channelsRes.data?.data || [] });
      setOutcomes(callStatsRes.data?.outcomeDistribution || { labels: [], data: [] });
      setActivities((activityRes.data || []).map((a: any) => ({ id: a.id, assistantName: a.assistantName, lastMessage: a.lastMessage, timestamp: a.timestamp })));
      setMessageActivity({ labels: msgActivityRes.data?.labels || [], data: msgActivityRes.data?.data || [] });
    } catch (e) {
      // Hard failure -> keep current state but do not inject mock
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, [selectedPeriod]);

  const conversationTrendData = useMemo(() => ({
    labels: trends.labels,
    datasets: [
      {
        label: 'Conversations',
        data: trends.data,
        fill: true,
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        tension: 0.4,
      }
    ]
  }), [trends]);

  const channelPerformanceData = useMemo(() => ({
    labels: channels.labels,
    datasets: [{
      label: 'Conversations by Channel',
      data: channels.data,
      backgroundColor: 'rgba(147, 51, 234, 0.6)',
      borderColor: 'rgba(147, 51, 234, 1)',
      borderWidth: 2,
      borderRadius: 8,
    }]
  }), [channels]);

  const outcomeData = useMemo(() => ({
    labels: outcomes.labels,
    datasets: [{
      data: outcomes.data,
      backgroundColor: outcomes.labels.map((_, i) => ['#22c55e','#3b82f6','#f59e0b','#ef4444','#a855f7','#06b6d4'][i % 6] + 'CC'),
      borderColor: outcomes.labels.map((_, i) => ['#22c55e','#3b82f6','#f59e0b','#ef4444','#a855f7','#06b6d4'][i % 6]),
      borderWidth: 2,
    }]
  }), [outcomes]);

  const messageActivityData = useMemo(() => ({
    labels: messageActivity.labels,
    datasets: [{
      label: 'Messages per Hour (Today)',
      data: messageActivity.data,
      fill: true,
      borderColor: 'rgb(34, 197, 94)',
      backgroundColor: 'rgba(34, 197, 94, 0.1)',
      tension: 0.3,
    }]
  }), [messageActivity]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { labels: { color: '#94A3B8' } } },
    scales: {
      x: { grid: { color: 'rgba(148, 163, 184, 0.1)' }, ticks: { color: '#94A3B8' } },
      y: { grid: { color: 'rgba(148, 163, 184, 0.1)' }, ticks: { color: '#94A3B8' } }
    }
  } as const;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-6">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="relative z-10 mb-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text text-transparent">Analytics Dashboard</h1>
            <p className="text-gray-400 mt-2">Real-time insights powered by your data</p>
          </div>
          <div className="flex gap-3">
            <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="px-4 py-2 rounded-lg bg-gray-800 text-gray-300 font-medium flex items-center gap-2 hover:bg-gray-700">
              <FiFilter size={18} />
              Filters
            </motion.button>
            <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="px-6 py-2 rounded-lg bg-gradient-to-r from-purple-500 to-pink-500 text-white font-medium flex items-center gap-2" onClick={() => window.open(`/api/analytics/report/export?period=${normalizePeriod(selectedPeriod)}&format=pdf`, '_blank')}>
              <FiDownload size={18} />
              Export Report
            </motion.button>
          </div>
        </div>
        <div className="flex gap-2">
          {['24h','7d','30d','90d','1y'].map(p => (
            <motion.button key={p} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={() => setSelectedPeriod(p)} className={`px-4 py-2 rounded-lg transition-all ${selectedPeriod===p ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}>
              {p}
            </motion.button>
          ))}
        </div>
      </motion.div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8 relative z-10">
        {[{
          title: 'Active Assistants', value: String(activeAssistants), icon: <FiUsers size={24} />, color: 'from-purple-500 to-pink-500'
        },{
          title: 'Total Conversations', value: totalConversations.toLocaleString(), icon: <FiMessageSquare size={24} />, color: 'from-blue-500 to-cyan-500'
        },{
          title: 'Messages Today', value: messagesToday.toLocaleString(), icon: <FiActivity size={24} />, color: 'from-green-500 to-emerald-500'
        },{
          title: 'Success Rate', value: `${successRate}%`, icon: <FiTrendingUp size={24} />, color: 'from-indigo-500 to-purple-500'
        },{
          title: 'Avg Response', value: `${Math.round(avgResponseMs/10)/100}s`, icon: <FiClock size={24} />, color: 'from-yellow-500 to-orange-500'
        },{
          title: 'Active Connections', value: String(activeConnections), icon: <FiActivity size={24} />, color: 'from-rose-500 to-red-500'
        }].map((m, i) => (
          <motion.div key={m.title} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i*0.05 }} whileHover={{ scale: 1.02, y: -3 }} className="relative overflow-hidden rounded-2xl p-6" style={{ background: 'rgba(30, 41, 59, 0.8)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255,255,255,0.1)' }}>
            <motion.div className={`absolute inset-0 opacity-10 bg-gradient-to-br ${m.color}`} animate={{ scale:[1,1.1,1], rotate:[0,5,0] }} transition={{ duration:8, repeat:Infinity, repeatType:'reverse' }} />
            <div className="relative z-10 flex items-center justify-between">
              <div className={`p-3 rounded-lg bg-gradient-to-br ${m.color}`}>{m.icon}</div>
              <div className="text-3xl font-bold text-white">{m.value}</div>
            </div>
            <div className="relative z-10 mt-2 text-gray-400 text-sm">{m.title}</div>
          </motion.div>
        ))}
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8 relative z-10">
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }} className="rounded-2xl p-6 h-96" style={{ background: 'rgba(30,41,59,0.8)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255,255,255,0.1)' }}>
          <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2"><FiTrendingUp className="text-blue-400" />Conversation Trends</h3>
          <div className="h-80"><Line data={conversationTrendData} options={chartOptions} /></div>
        </motion.div>

        <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.25 }} className="rounded-2xl p-6 h-96" style={{ background: 'rgba(30,41,59,0.8)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255,255,255,0.1)' }}>
          <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2"><FiBarChart2 className="text-purple-400" />Channel Performance</h3>
          <div className="h-80"><Bar data={channelPerformanceData} options={chartOptions} /></div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="rounded-2xl p-6 h-96" style={{ background: 'rgba(30,41,59,0.8)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255,255,255,0.1)' }}>
          <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2"><FiPieChart className="text-green-400" />Call Outcomes</h3>
          <div className="h-80"><Doughnut data={outcomeData} options={{ ...chartOptions, plugins: { ...chartOptions.plugins, legend: { position: 'right', labels: { color: '#94A3B8' } } } }} /></div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }} className="rounded-2xl p-6 h-96" style={{ background: 'rgba(30,41,59,0.8)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255,255,255,0.1)' }}>
          <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2"><FiActivity className="text-yellow-400" />Message Activity (Today)</h3>
          <div className="h-80"><Line data={messageActivityData} options={chartOptions} /></div>
        </motion.div>
      </div>

      {/* Live Activity Feed */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="rounded-2xl p-6 relative z-10" style={{ background: 'rgba(30,41,59,0.8)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255,255,255,0.1)' }}>
        <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2"><FiActivity className="text-green-400" />Live Activity Feed</h3>
        <div className="space-y-3">
          {activities.length === 0 && <p className="text-gray-400">No recent activity</p>}
          {activities.map((a) => (
            <div key={a.id} className="flex items-center gap-4 p-3 rounded-lg bg-gray-800/50">
              <div className="p-2 rounded-lg bg-blue-500/20"><FiActivity className="text-blue-400" /></div>
              <div className="flex-1">
                <p className="text-white font-medium">{a.assistantName}</p>
                <p className="text-gray-400 text-sm truncate">{a.lastMessage}</p>
              </div>
              <span className="text-xs text-gray-500">{new Date(a.timestamp).toLocaleString()}</span>
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  );
};

export default Analytics;
